interface SlotStatus {
  isLocked: boolean
  isMyLock: boolean
  timeRemaining: number
  lockedBy: string | null
}

interface SlotActionButtonsProps {
  slotId: string
  volunteerName: string | null
  volunteerEmail: string | null
  className: string
  status: SlotStatus
  isMyRegistration: boolean
  canDelete: boolean
  isHolidayOrBreak: boolean
  isShabbat: boolean
  isCampDay: boolean
  isPastDate: boolean
  onRegister: (slotId: string) => void
  onDelete: (slotId: string) => void
  onAddToCalendar: () => void
}

export function SlotActionButtons({
  slotId,
  volunteerName,
  className,
  status,
  isMyRegistration,
  canDelete,
  isHolidayOrBreak,
  isShabbat,
  isCampDay,
  isPastDate,
  onRegister,
  onDelete,
  onAddToCalendar
}: SlotActionButtonsProps) {
  if ((isHolidayOrBreak || isShabbat) && !isCampDay) {
    return (
      <span className="text-sm text-gray-500 font-hebrew font-medium">
        אין תורנות
      </span>
    )
  }

  if (isPastDate && !volunteerName) {
    return (
      <span className="text-sm text-gray-400 font-hebrew font-medium">
        עבר
      </span>
    )
  }

  if (volunteerName) {
    return (
      <div className="flex items-center justify-start gap-2">
        {isMyRegistration && (
          <button
            onClick={onAddToCalendar}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 md:p-2 rounded-full transition-colors min-h-[44px] min-w-[44px] touch-manipulation flex items-center justify-center focus:outline-none"
            title="הוסף ליומן"
            aria-label="הוסף ליומן"
          >
            <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        )}

        {!isMyRegistration && !canDelete && (
          <div
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-50 p-2 md:p-2 rounded-full transition-colors min-h-[44px] min-w-[44px] touch-manipulation flex items-center justify-center"
            title="תפוס על ידי משתמש אחר"
            aria-label="תפוס על ידי משתמש אחר"
          >
            <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        )}

        {canDelete && (
          <button
            onClick={() => onDelete(slotId)}
            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 md:p-2 rounded-full transition-colors min-h-[44px] min-w-[44px] touch-manipulation flex items-center justify-center focus:outline-none"
            title={isMyRegistration ? "בטל רישום" : "מחק תורנות (אדמין)"}
            aria-label={isMyRegistration ? "בטל רישום" : "מחק תורנות"}
          >
            <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    )
  }

  if (status.isLocked) {
    return status.isMyLock ? (
      <div className="flex items-center justify-start gap-2 text-blue-600">
        <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span className="text-sm font-hebrew font-medium">
          בתהליך רישום
        </span>
      </div>
    ) : (
      <div className="flex items-center justify-start gap-2 text-orange-600">
        <svg className="w-4 h-4 text-orange-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-hebrew font-medium">
          רישום אחר ({status.timeRemaining}ש)
        </span>
      </div>
    )
  }

  const isOpenClass = !className || className === 'פתוח' || className === 'open'
  const isDisabled = isOpenClass
  const disabledReason = isOpenClass
    ? 'לא ניתן להירשם - כיתה לא משויכת'
    : 'הירשם לתורנות'

  return (
    <div className="flex items-center justify-start gap-3">
      <button
        onClick={() => !isDisabled && onRegister(slotId)}
        disabled={isDisabled}
        className={`btn-register font-hebrew min-h-[44px] min-w-[60px] md:min-w-[80px] touch-manipulation ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        aria-label={disabledReason}
        title={disabledReason}
      >
        הירשם
      </button>
    </div>
  )
}
